import { PlaceNode, PlaceNodeTypeEnum } from '@ulysses-inc/harami_api_client'
import { FormikProps, useFormik } from 'formik'
import { UUID } from 'src/exShared/util/uuid'
import { useMutationAddPlaceGroup } from 'src/features/places/api/useMutationAddPlaceGroup'
import { useMutationUpdatePlaceGroup } from 'src/features/places/api/useMutationUpdatePlaceGroup'
import Yup from 'src/features/validation/yup'
import type { FlatNode } from './PlaceGroupsProps'

export interface PlaceGroupForm {
  nodeId?: string
  name: string
  parentNodeId?: string
  nodeIds: string[]
}

const validationSchema = Yup.object().shape({
  name: Yup.string().required().max(255).label('現場グループ名'),
})

export const useFormikProps = ({
  activeNode,
  activeParentNode,
  onClose,
}: {
  activeNode: FlatNode | undefined
  activeParentNode: PlaceNode | undefined
  onClose: () => void
}): FormikProps<PlaceGroupForm> & { isPending: boolean } => {
  const addMutation = useMutationAddPlaceGroup()
  const updateMutation = useMutationUpdatePlaceGroup()

  const formik = useFormik<PlaceGroupForm>({
    initialValues: {
      nodeId: activeNode?.uuid || undefined,
      name: activeNode?.placeGroup?.name || '',
      parentNodeId: activeParentNode?.id.toString() || undefined,
      nodeIds:
        (activeNode?.nodes as PlaceNode[])?.map(({ id }) => id.toString()) ||
        [],
    },
    validationSchema: validationSchema,
    onSubmit: (form, formikBag) => {
      const groupNodeIds: string[] = ((activeNode?.nodes as PlaceNode[]) || [])
        .filter(({ type }) => type === PlaceNodeTypeEnum.PlaceGroup)
        .map(({ id }) => id.toString())
      const nodeIds = [...(form.nodeIds ?? []), ...groupNodeIds].filter(
        (nodeId, i, self) => self.indexOf(nodeId) === i,
      )

      const placeNode: PlaceNode = {
        id: activeNode?.id ?? 0,
        uuid: activeNode?.uuid ?? UUID(),
        type: PlaceNodeTypeEnum.PlaceGroup,
        isRootNode:
          form.parentNodeId === 'root' || form.parentNodeId === undefined
            ? 1
            : 0,
        placeGroup: {
          id: activeNode?.placeGroup?.id || undefined,
          name: form.name,
        },
        parentNodes:
          form.parentNodeId !== 'root' && form.parentNodeId
            ? ([{ id: Number(form.parentNodeId) }] as PlaceNode[])
            : [],
        nodes: nodeIds.map(strId => ({
          id: Number(strId),
        })) as PlaceNode[],
      }

      const onMutationSuccess = () => {
        formikBag.resetForm()
        onClose()
      }

      const isEdit = activeNode !== undefined
      if (!isEdit) {
        addMutation.mutate({ placeNode }, { onSuccess: onMutationSuccess })
      } else {
        updateMutation.mutate(
          { placeNodeId: placeNode.uuid, placeNode },
          { onSuccess: onMutationSuccess },
        )
      }
    },
  })

  return {
    ...formik,
    isPending: addMutation.isPending || updateMutation.isPending,
  }
}
